import i18n from "@/i18n";

/**
 * Translate a canonical English game-data label (skill, weapon, occupation)
 * into the active UI language. Falls back to the original label when no
 * translation exists, so this is always safe to call.
 */
function translateGameData(
  kind: "skills" | "weapons" | "occupations",
  label: string | null | undefined
): string {
  if (!label) return "";
  const key = `${kind}.${label}`;
  const translated = i18n.t(key, { ns: "gamedata", defaultValue: label });
  return typeof translated === "string" ? translated : label;
}

export const tSkill = (label: string | null | undefined) => translateGameData("skills", label);
export const tWeapon = (label: string | null | undefined) => translateGameData("weapons", label);
export const tOccupation = (label: string | null | undefined) => translateGameData("occupations", label);

/**
 * Translate a skill name that may include a parenthesized specialization,
 * e.g. "Art/Craft (Photography)" or "Pilot (Boat)". Tries the full canonical
 * label first; if no translation exists, falls back to translating the parent
 * name and (when the specialization itself is a known canonical token) the
 * specialization, joining them as "Parent (Spec)". User-entered free-text
 * specializations are preserved verbatim.
 */
export const tSkillName = (label: string | null | undefined): string => {
  if (!label) return "";
  // Try the full label first — most predefined specializations are keyed.
  const direct = i18n.t(`skills.${label}`, { ns: "gamedata", defaultValue: "" });
  if (typeof direct === "string" && direct.trim().length > 0 && direct !== label) {
    return direct;
  }
  const match = label.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (!match) return tSkill(label);
  const parent = match[1].trim();
  const spec = match[2].trim();
  const translatedParent = tSkill(parent);
  // Translate the specialization if it is itself a canonical skill token
  // (e.g. "Acting", "Photography", "Boat"); otherwise leave the user's text alone.
  const specKey = i18n.t(`skills.${spec}`, { ns: "gamedata", defaultValue: "" });
  const translatedSpec =
    typeof specKey === "string" && specKey.trim().length > 0 ? specKey : spec;
  // Lowercase generic markers
  const lowerSpec = spec.toLowerCase();
  if (lowerSpec === "any" || lowerSpec === "other" || lowerSpec === "own") {
    const genericKey = i18n.t(`skills.specializationMarkers.${lowerSpec}`, {
      ns: "gamedata",
      defaultValue: spec,
    });
    return `${translatedParent} (${typeof genericKey === "string" ? genericKey : spec})`;
  }
  return `${translatedParent} (${translatedSpec})`;
};

/**
 * Translate flavour prose tied to a canonical English occupation name.
 * Falls back to the supplied raw English string if no translation exists
 * (or if the translation entry is an empty placeholder).
 */
function translateOccupationProse(
  section: "occupationDescriptions" | "occupationContacts",
  occupationName: string | null | undefined,
  fallback?: string | null
): string {
  const raw = (fallback ?? "").toString();
  if (!occupationName) return raw;
  const key = `${section}.${occupationName}`;
  const translated = i18n.t(key, { ns: "gamedata", defaultValue: "" });
  const value = typeof translated === "string" ? translated : "";
  return value.trim().length > 0 ? value : raw;
}

export const tOccupationDescription = (
  occupationName: string | null | undefined,
  fallback?: string | null,
) => translateOccupationProse("occupationDescriptions", occupationName, fallback);

export const tOccupationContacts = (
  occupationName: string | null | undefined,
  fallback?: string | null,
) => translateOccupationProse("occupationContacts", occupationName, fallback);

/**
 * Translate the English placeholder/hint strings that come from skill
 * normalization (`normalize-skills.ts`) and occupation data
 * (`occupation-skills.ts` and *-occupations.json). Recognized canonical
 * strings are mapped to existing `widgets` keys. "X, Y, or Z" skill lists
 * are split, each skill name is translated via `tSkill`, and rejoined with
 * the localized "or" connector. Anything else is returned verbatim.
 */
const HINT_KEY_MAP: Record<string, string> = {
  "Enter Art/Craft": "skills.specialization.artCraft",
  "Enter Language": "skills.specialization.language",
  "Enter Science specialization": "skills.specialization.science",
  "Language specialization": "skills.specialization.language",
  "Art/Craft specialization": "skills.specialization.artCraft",
  "Select any skill": "skills.selectAnySkill",
  "Select an interpersonal skill": "skills.selectInterpersonalSkill",
  "Select a skill": "skills.selectASkill",
  "Player's choice": "skills.hints.playerChoice",
};

export const tHint = (hint?: string | null): string => {
  if (!hint) return "";
  const trimmed = hint.trim();
  if (!trimmed) return "";
  const mapped = HINT_KEY_MAP[trimmed];
  if (mapped) {
    const v = i18n.t(mapped, { ns: "widgets", defaultValue: trimmed });
    return typeof v === "string" ? v : trimmed;
  }
  // Parse "A, B, C, or D" lists of skill names.
  const orMatch = trimmed.match(/^(.+?),?\s+or\s+(.+)$/i);
  if (orMatch) {
    const lead = orMatch[1]
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const last = orMatch[2].trim();
    const all = [...lead, last];
    const orWordRaw = i18n.t("skills.hints.or", { ns: "widgets", defaultValue: "or" });
    const orWord = typeof orWordRaw === "string" ? orWordRaw : "or";
    const translated = all.map((s) => tSkill(s));
    if (translated.length === 1) return translated[0];
    const head = translated.slice(0, -1).join(", ");
    return `${head}, ${orWord} ${translated[translated.length - 1]}`;
  }
  return trimmed;
};